import React, { useState, useEffect } from "react";
import logo from '../utils/logo.png';

import { FaUsers, FaWarehouse, FaChartLine, FaFileInvoice, FaFileSignature, FaReceipt } from "react-icons/fa";
import { FaSackDollar } from "react-icons/fa6";
import { FiLogOut } from "react-icons/fi";
import { IoStatsChartSharp } from "react-icons/io5";

import CustomersTab from "./tabs/CustomersTab";
import GarageSitesTab from "./tabs/GarageSitesTab";
import MonthlyActivityTab from "./tabs/MonthlyActivityTab";
import BillingInvoiceTab from "./tabs/BillingInvoiceTab";
import ApprovedInvoicesTab from "./tabs/ApprovedInvoicesTab";
import HardwareInvoiceTab from "./tabs/HardwareInvoiceTab";
import QuotationTab from "./tabs/QuotationTab";
import ExpenditureTab from "./tabs/ExpenditureTab";
import ProfitMarginTab from "./tabs/ProfitMarginTab";
import ManageItemsTab from "./tabs/ManageItemsTab";


import axios from "../auth/auth";

/* CHART.JS */
import { Bar, Line } from "react-chartjs-2";
import { SiOpensourcehardware } from "react-icons/si";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Tooltip,
    Legend,
    Title
} from "chart.js";
import { BsFileEarmarkCheckFill } from "react-icons/bs";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Tooltip,
    Legend,
    Title
);

export default function FacilityMasterHome() {
    const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const userRole = sessionStorage.getItem("role");

    const allTabs = [
        { id: "dashboard", label: "Dashboard", icon: <IoStatsChartSharp /> },
        { id: "customers", label: "Customers", icon: <FaUsers /> },
        { id: "garages", label: "Garage Sites", icon: <FaWarehouse /> },
        { id: "monthly", label: "Monthly Activity", icon: <FaChartLine /> },
        { id: "billing", label: "Billing Invoice", icon: <FaFileInvoice /> },
        { id: "approved_invoices", label: "Invoices Comparision", icon: <BsFileEarmarkCheckFill /> },
        { id: "quotations", label: "Quotations", icon: <FaFileSignature /> },
        { id: "hardware_invoices", label: "Hardware Invoice", icon: <SiOpensourcehardware /> },
        { id: "manage_items", label: "Manage Items", icon: <SiOpensourcehardware /> },
        { id: "expenditure", label: "Expenditure Log", icon: <FaReceipt /> },
        { id: "profit_margin", label: "Profit Margin", icon: <FaSackDollar /> }
    ];

    // Check if a tab is enabled for the current role
    const isTabEnabled = (tabId) => {
        if (userRole === "Admin" || userRole === "Accounts") return true;

        if (userRole === "Sales") {
            return ["customers", "garages", "expenditure", "quotations", "manage_items"].includes(tabId);
        }

        // if (userRole === "Accounts") {
        //     return ["dashboard", "expenditure", "profit_margin"].includes(tabId);
        // }

        return false;
    };

    const invoiceTabs = allTabs.filter(tab =>
        ["monthly", "billing", "approved_invoices"].includes(tab.id) && isTabEnabled(tab.id)
    );

    const quotationTabs = allTabs.filter(tab =>
        ["quotations", "hardware_invoices", "manage_items"].includes(tab.id) && isTabEnabled(tab.id)
    );

    // NORMAL TOP TABS
    const mainTabs = allTabs.filter(tab =>
        ["dashboard", "customers", "garages", "expenditure", "profit_margin"].includes(tab.id) && isTabEnabled(tab.id)
    );

    // close dropdown when clicking outside
    useEffect(() => {
        const handleClick = () => setOpenDropdown(null);
        window.addEventListener("click", handleClick);
        return () => window.removeEventListener("click", handleClick);
    }, []);

    // Set default active tab based on user role
    const getDefaultTab = () => {
        if (userRole === "Admin") return "dashboard";
        if (userRole === "Sales") return "customers";
        if (userRole === "Accounts") return "dashboard";
        return "dashboard";
    };

    const [activeTab, setActiveTab] = useState(getDefaultTab());
    const [customers, setCustomers] = useState([]);
    const [garages, setGarages] = useState([]);
    const [invoiceRevenue, setInvoiceRevenue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCustomerId, setSelectedCustomerId] = useState("ALL");
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [openDropdown, setOpenDropdown] = useState(null);

    // useEffect(() => {
    //     axios.get(`${BASE_URL}/customers/`)
    //         .then(res => setCustomers(res.data))
    //         .catch(() => setCustomers([]));

    //     axios.get(`${BASE_URL}/garages/`)
    //         .then(res => setGarages(res.data))
    //         .catch(() => setGarages([]));

    //     axios.get(`${BASE_URL}/invoice-revenue-summary`)
    //         .then(res => setInvoiceRevenue(res.data))
    //         .catch(() => setInvoiceRevenue([]));
    // }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const [customersRes, garagesRes, revenueRes] = await Promise.all([
                    axios.get("/customers/"),
                    axios.get("/garages/"),
                    axios.get("/invoice-revenue-summary")
                ]);

                setCustomers(customersRes.data || []);
                setGarages(garagesRes.data || []);
                setInvoiceRevenue(revenueRes.data || []);

            } catch (err) {
                console.error("Dashboard load failed", err);
                setCustomers([]);
                setGarages([]);
                setInvoiceRevenue([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Customer name map for dropdown
    const sortedCustomers = customers
        .map(c => ({
            id: c.CustomerID,
            name: (c.CustomerName || "").trim()
        }))
        .sort((a, b) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );



    const monthOrderMap = {
        jan: 1,
        feb: 2,
        mar: 3,
        apr: 4,
        may: 5,
        jun: 6,
        jul: 7,
        aug: 8,
        sep: 9,
        oct: 10,
        nov: 11,
        dec: 12
    };

    const parseMonthValue = (monthValue) => {
        if (!monthValue) return 0;
        const cleaned = monthValue.toString().trim();

        // Support numeric formats like 01-2026, 12/2025, 1-25
        const numericMatch = cleaned.match(/^(\d{1,2})[-\/](\d{2,4})$/);
        if (numericMatch) {
            const month = Number(numericMatch[1]);
            let year = Number(numericMatch[2]);
            if (year < 100) {
                year += 2000;
            }
            if (month >= 1 && month <= 12) {
                return new Date(year, month - 1, 1).getTime();
            }
        }

        // Support text formats like Jan 2026, January-2026
        const normalized = cleaned.replace(/[-]/g, " ");
        const parsedDate = Date.parse(normalized);
        if (!isNaN(parsedDate)) return parsedDate;

        const parts = normalized.split(/\s+/);
        if (parts.length >= 2) {
            const monthKey = parts[0].slice(0, 3).toLowerCase();
            const monthIndex = monthOrderMap[monthKey];
            const year = Number(parts[1]);
            if (monthIndex && !isNaN(year)) {
                return new Date(year, monthIndex - 1, 1).getTime();
            }
        }
        return 0;
    };
    // const months = Array.from(new Set(invoiceRevenue.map(r => r.month).filter(Boolean))).sort(
    //     (a, b) => parseMonthValue(a) - parseMonthValue(b)
    // );

    const months = Array.from(
        new Set(invoiceRevenue.map(r => r.month).filter(Boolean))
    ).sort((a, b) => parseMonthValue(b) - parseMonthValue(a));





    const latestMonths = months.slice(0, 5);
    const displayedMonths = selectedMonth ? [selectedMonth] : latestMonths;

    useEffect(() => {
        if (selectedMonth && !months.includes(selectedMonth)) {
            setSelectedMonth(null);
        }
    }, [months, selectedMonth]);

    // Aggregate monthly revenue
    const monthlyRevenue = displayedMonths.map(month => {
        if (selectedCustomerId === "ALL") {
            // sum all customers for this month
            return invoiceRevenue
                .filter(r => r.month === month)
                .reduce((sum, r) => sum + Number(r.total_revenue), 0);
        } else {
            const record = invoiceRevenue.find(
                r => r.customer_id.toString() === selectedCustomerId && r.month === month
            );
            return record ? Number(record.total_revenue) : 0;
        }
    });

    const barChartData = {
        labels: displayedMonths,
        datasets: [
            {
                label: "Revenue ($)",
                data: monthlyRevenue,
                backgroundColor: "#3b82f6",
                borderRadius: 6
            }
        ]
    };

    const lineChartData = {
        labels: displayedMonths,
        datasets: [
            {
                label: "Revenue Trend ($)",
                data: monthlyRevenue,
                borderColor: "#f97316",
                backgroundColor: "rgba(249,115,22,0.2)",
                tension: 0.4,
                fill: true,
                pointRadius: 5
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        return `$${context.raw.toLocaleString()}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: v => `$${v}`
                }
            }
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-50">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-500 text-sm">Loading data...</p>
                </div>
            </div>
        );
    }


    return (
        <div className="min-h-screen bg-gray-50 text-gray-700">
            {/* HEADER */}
            <div className="bg-white shadow-sm px-6 py-4 border-b">
                <div className="flex items-center justify-between">

                    {/* LEFT SIDE */}
                    <div className="flex items-center gap-3">
                        <img src={logo} alt="Logo" className="h-12" />
                        <div>
                            <h1 className="text-lg font-semibold">
                                ParkEngage Facility Master
                            </h1>
                            <p className="text-xs text-gray-500">
                                Digital at Every Touchpoint™
                            </p>
                        </div>
                    </div>

                    {/* RIGHT SIDE */}
                    <button
                        onClick={() => {
                            const confirmLogout = window.confirm("Are you sure you want to logout?");
                            if (confirmLogout) {
                                sessionStorage.clear();
                                window.location.href = "/";
                            }
                        }}
                        className="flex items-center gap-2 
                 border border-red-500 text-red-500 
                 px-4 py-2 rounded-lg 
                 hover:bg-red-500 hover:text-white 
                 transition-all duration-200 
                 focus:outline-none focus:ring-2 focus:ring-red-300"
                    >
                        <FiLogOut className="text-lg" />
                        Logout
                    </button>

                </div>
            </div>

            {/* TABS */}
            <div className="bg-white shadow-sm px-6">
                <div className="flex space-x-8 items-center">

                    {/* MAIN TABS */}
                    {mainTabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-3 flex items-center gap-2 border-b-2 transition-all ${
                                activeTab === tab.id
                                    ? "border-blue-600 text-blue-600 font-semibold"
                                    : "border-transparent text-gray-600"
                            }`}
                        >
                            {tab.icon}
                            <span>{tab.label}</span>
                        </button>
                    ))}

                    {/* INVOICES DROPDOWN */}
                    {invoiceTabs.length > 0 && (
                        <div
                            className="relative"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={() =>
                                    setOpenDropdown(openDropdown === "invoices" ? null : "invoices")
                                }
                                className="py-3 flex items-center gap-2 text-gray-600 hover:text-blue-600"
                            >
                                <span>Invoices</span> ▾
                            </button>

                            {openDropdown === "invoices" && (
                                <div className="absolute top-full mt-2 w-64 bg-white shadow-lg rounded-lg border z-50">
                                    {invoiceTabs.map(tab => (
                                        <div
                                            key={tab.id}
                                            onClick={() => (setActiveTab(tab.id), setOpenDropdown(null))}
                                            className={`px-4 py-2 flex items-center gap-2 cursor-pointer hover:bg-gray-100 ${activeTab === tab.id ? "bg-blue-50 text-blue-600 font-medium" : ""}`}
                                        >
                                            {tab.icon}
                                            <span>{tab.label}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* QUOTATIONS DROPDOWN */}
                    {quotationTabs.length > 0 && (
                        <div
                            className="relative"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={() =>
                                    setOpenDropdown(openDropdown === "quotations" ? null : "quotations")
                                }
                                className="py-3 flex items-center gap-2 text-gray-600 hover:text-blue-600"
                            >
                                <span>Quotations</span> ▾
                            </button>

                            {openDropdown === "quotations" && (
                                <div className="absolute top-full mt-2 w-64 bg-white shadow-lg rounded-lg border z-50">
                                    {quotationTabs.map(tab => (
                                        <div
                                            key={tab.id}
                                            onClick={() => (setActiveTab(tab.id), setOpenDropdown(null))}
                                            className={`px-4 py-2 flex items-center gap-2 cursor-pointer hover:bg-gray-100 ${activeTab === tab.id ? "bg-blue-50 text-blue-600 font-medium" : ""}`}
                                        >
                                            {tab.icon}
                                            <span>{tab.label}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>

            <div className="p-6">
                {activeTab === "dashboard" && (
                    <>
                        {/* KPI CARDS
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                            <div className="bg-white p-6 rounded-xl shadow">
                                <p>Total Customers</p>
                                <p className="text-4xl font-bold">{customers.length}</p>
                            </div>

                            <div className="bg-white p-6 rounded-xl shadow">
                                <p>Garage Sites</p>
                                <p className="text-4xl font-bold">{garages.length}</p>
                            </div>
                        </div> */}


                        {/* KPI CARDS */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">

                            {/* TOTAL CUSTOMERS */}
                            <div className="bg-white shadow-lg rounded-xl p-6 flex justify-between items-center border border-gray-100">
                                <div>
                                    <p className="text-gray-500">Total Customers</p>
                                    <p className="text-4xl font-bold">{customers.length}</p>
                                    <p className="text-sm text-gray-400 mt-1">
                                        Active customer accounts
                                    </p>
                                </div>

                                <div className="bg-blue-50 p-4 rounded-full shadow-sm">
                                    <FaUsers className="text-blue-500 text-3xl" />
                                </div>
                            </div>

                            {/* GARAGE SITES */}
                            <div className="bg-white shadow-lg rounded-xl p-6 flex justify-between items-center border border-gray-100">
                                <div>
                                    <p className="text-gray-500">Garage Sites</p>
                                    <p className="text-4xl font-bold">{garages.length}</p>
                                    <p className="text-sm text-gray-400 mt-1">
                                        {garages.filter(g => g.Status === "Active").length} active, {garages.filter(g => g.Status === "Maintenance").length} maintenance, {garages.filter(g => g.Status === "Inactive").length} inactive
                                    </p>
                                </div>

                                <div className="bg-orange-50 p-4 rounded-full shadow-sm">
                                    <FaWarehouse className="text-orange-500 text-3xl" />
                                </div>
                            </div>
                        </div>


                        {/* DROPDOWN + BAR CHART */}
                        <div className="bg-white shadow-lg rounded-xl p-6 mb-6">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                                <div>
                                    <h3 className="text-xl font-semibold">Monthly Revenue by Customer</h3>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                    <label className="flex items-center gap-2">
                                        <span className="text-sm text-gray-600">Customer</span>
                                        <select
                                            className="border rounded px-3 py-2"
                                            value={selectedCustomerId}
                                            onChange={e => setSelectedCustomerId(e.target.value)}
                                        >
                                            <option value="ALL">All Customers</option>
                                            {sortedCustomers.map(c => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <span className="text-sm text-gray-600">Month</span>
                                        <select
                                            className="border rounded px-3 py-2"
                                            value={selectedMonth || ""}
                                            onChange={e => setSelectedMonth(e.target.value || null)}
                                        >
                                            <option value="" disabled>Select month</option>
                                            {months.map(month => (
                                                <option key={month} value={month}>
                                                    {month}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    {selectedMonth && (
                                        <button
                                            type="button"
                                            onClick={() => setSelectedMonth(null)}
                                            className="bg-blue-600 text-white rounded px-3 py-2 hover:bg-blue-700"
                                        >
                                            Show latest 5 months
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="h-72">
                                <Bar data={barChartData} options={chartOptions} />
                            </div>
                        </div>

                        {/* LINE CHART */}
                        <div className="bg-white shadow-lg rounded-xl p-6">
                            <h3 className="text-xl font-semibold mb-4">Revenue Trend</h3>
                            <div className="h-72">
                                <Line data={lineChartData} options={chartOptions} />
                            </div>
                        </div>
                    </>
                )}

                {activeTab === "customers" && <CustomersTab />}
                {activeTab === "garages" && <GarageSitesTab />}
                {activeTab === "monthly" && <MonthlyActivityTab />}
                {activeTab === "billing" && <BillingInvoiceTab />}
                {activeTab === "approved_invoices" && <ApprovedInvoicesTab />}
                {activeTab === "quotations" && <QuotationTab />}
                {activeTab === "hardware_invoices" && <HardwareInvoiceTab />}
                {activeTab === "manage_items" && <ManageItemsTab />}
                {activeTab === "expenditure" && <ExpenditureTab garages={garages} />}
                {activeTab === "profit_margin" && <ProfitMarginTab customers={customers} garages={garages} />}
            </div>
        </div>
    );
}
